import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import NotificationTextCollection from '../Notifications/TextCollection';
import NotificationTextFile from '../Notifications/TextFile';

import {
  getCurrentUser,
  userIsAdmin,
} from '../../helpers/Auth';
import { getCollectionUrl } from '../../helpers/Collections';

const ActionsItemText = ({ action: actionObject, profile }) => {
  const intl = useIntl();

  const {
    type,
    action,
    collection,
    file,
    files,
    user,
    data,
  } = actionObject;

  const currentUser = getCurrentUser();
  const linksEnabled = userIsAdmin() || (currentUser && currentUser.uuid === profile.uuid);

  switch (type) {
    case 'upload': {
      return (
        <span>
          <i className="far fa-upload" />
          {intl.formatMessage({ defaultMessage: 'Uploaded' })}{' '}
          {(linksEnabled && files) ? (
            <Link to={`/uploads/${data.uuid}`} target="_blank">
              {intl.formatMessage({
                defaultMessage: '{files} {files, plural, one {file} other {files}}',
              }, { files })}
            </Link>
          ) : (
            <strong>
              {intl.formatMessage({
                defaultMessage: '{files} {files, plural, one {file} other {files}}',
              }, { files })}
            </strong>
          )}
        </span>
      );
    }

    case 'edit_files': {
      return (
        <span>
          <i className="far fa-pen" />
          {intl.formatMessage({ defaultMessage: 'Edited' })}{' '}
          {(linksEnabled && files) ? (
            <Link to={`/edits/${data.uuid}`} target="_blank">
              {intl.formatMessage({
                defaultMessage: '{files} {files, plural, one {file} other {files}}',
              }, { files })}
            </Link>
          ) : (
            <strong>
              {intl.formatMessage({
                defaultMessage: '{files} {files, plural, one {file} other {files}}',
              }, { files })}
            </strong>
          )}
        </span>
      );
    }

    case 'create_link_cards': {
      return (
        <span>
          <i className="far fa-link" />
          {intl.formatMessage({ defaultMessage: 'Created' })}{' '}
          {(linksEnabled && files) ? (
            <Link to={`/uploads/${data.uuid}`} target="_blank">
              {intl.formatMessage({
                defaultMessage: '{files} link {files, plural, one {card} other {cards}}',
              }, { files })}
            </Link>
          ) : (
            <strong>
              {intl.formatMessage({
                defaultMessage: '{files} link {files, plural, one {card} other {cards}}',
              }, { files })}
            </strong>
          )}
        </span>
      );
    }

    case 'like': {
      if (data.type === 'file') {
        return (
          <span>
            <i className="far fa-star" />
            {intl.formatMessage({ defaultMessage: 'Liked file' })}{' '}
            <NotificationTextFile file={file} />
          </span>
        );
      }

      if (data.type === 'collection') {
        return (
          <span>
            <i className="far fa-star" />
            {intl.formatMessage({ defaultMessage: 'Liked collection' })}{' '}
            <NotificationTextCollection collection={collection} />
          </span>
        );
      }

      return null;
    }

    case 'sharelink': {
      switch (action) {
        case 'create': {
          return (
            <span>
              <i className="far fa-share-alt" />
              {intl.formatMessage({ defaultMessage: 'Created' })}{' '}
              {linksEnabled ? (
                <Link to={`/shares/${data.uuid}`} target="_blank">
                  {intl.formatMessage({ defaultMessage: 'share link' })}
                </Link>
              ) : (
                <strong>
                  {intl.formatMessage({ defaultMessage: 'share link' })}
                </strong>
              )}
            </span>
          );
        }

        case 'delete': {
          return (
            <span>
              <i className="far fa-share-alt" />
              {intl.formatMessage({ defaultMessage: 'Deleted share link' })}
              {data.uuid && (
                <>
                  {' '}{intl.formatMessage({ defaultMessage: '(ID: {id})' }, { id: data.uuid })}
                </>
              )}
            </span>
          );
        }

        default:
          return null;
      }
    }

    case 'comment': {
      const commentedFile = file || data.file;
      return (
        <span>
          <i className="far fa-share-alt" />
          {action === 'create' && (
            intl.formatMessage({ defaultMessage: 'Posted' })
          )}
          {action === 'update' && (
            intl.formatMessage({ defaultMessage: 'Edited' })
          )}
          {action === 'delete' && (
            intl.formatMessage({ defaultMessage: 'Deleted' })
          )}{' '}
          {commentedFile ? (
            <>
              <NotificationTextFile
                file={commentedFile}
                url={`/files/${commentedFile.uuid}#comment-${data.id}`}
                linkText={intl.formatMessage({ defaultMessage: 'comment' })}
              />
              {' '}{intl.formatMessage({ defaultMessage: 'in file' })}{' '}
              <NotificationTextFile file={commentedFile} />
            </>
          ) : (
            intl.formatMessage({ defaultMessage: 'comment' })
          )}
        </span>
      );
    }

    case 'user': {
      switch (action) {
        case 'create': {
          return (
            <span>
              <i className="far fa-user" />
              {intl.formatMessage({ defaultMessage: 'Invited user' })}{' '}
              {user && (
                <Link to={`/profile/${user.uuid}`} target="_blank">
                  {user.name}
                </Link>
              )}
            </span>
          );
        }

        case 'update':
        case 'update_password': {
          return (
            <span>
              <i className="far fa-user" />
              {action === 'update' && (
                intl.formatMessage({ defaultMessage: 'Updated' })
              )}
              {action === 'update_password' && (
                intl.formatMessage({ defaultMessage: 'Changed' })
              )}{' '}
              {(user && user.uuid !== currentUser.uuid) && (
                <>
                  <Link to={`/profile/${user.uuid}`} target="_blank">
                    {user.name}&apos;s
                  </Link>{' '}
                </>
              )}
              {action === 'update_password' ? (
                intl.formatMessage({ defaultMessage: 'password' })
              ) : (
                intl.formatMessage({ defaultMessage: 'profile' })
              )}
            </span>
          );
        }

        default:
          return null;
      }
    }

    case 'collection': {
      switch (action) {
        case 'create':
        case 'update':
        case 'update_files_order':
        case 'update_collections_order':
        case 'update_permissions':
        case 'archive':
        case 'unarchive':
        case 'set_featured_image': {
          return (
            <span>
              <i className="far fa-layer-group" />
              {action === 'create' && (
                intl.formatMessage({ defaultMessage: 'Created Collection' })
              )}
              {action === 'update' && (
                intl.formatMessage({ defaultMessage: 'Edited Collection' })
              )}
              {action === 'update_files_order' && (
                intl.formatMessage({ defaultMessage: 'Changed file order in Collection' })
              )}
              {action === 'update_collections_order' && (
                intl.formatMessage({ defaultMessage: 'Changed Collection order in Collection' })
              )}
              {action === 'update_permissions' && (
                intl.formatMessage({ defaultMessage: 'Changed access for Collection' })
              )}
              {action === 'archive' && (
                intl.formatMessage({ defaultMessage: 'Archived Collection' })
              )}
              {action === 'unarchive' && (
                intl.formatMessage({ defaultMessage: 'Unarchived Collection' })
              )}
              {action === 'set_featured_image' && (
                intl.formatMessage({ defaultMessage: 'Changed thumbnail for Collection' })
              )}
              {' '}
              {collection && (
                <>
                  {linksEnabled ? (
                    <Link to={`/collections/${collection.uuid}`} target="_blank">
                      {collection.name}
                    </Link>
                  ) : (
                    <strong>
                      {collection.name}
                    </strong>
                  )}
                </>
              )}
            </span>
          );
        }

        case 'duplicate': {
          return (
            <span>
              <i className="far fa-layer-group" />
              {intl.formatMessage({ defaultMessage: 'Duplicated Collection' })}
              {' '}
              {collection && (
                <>
                  {linksEnabled ? (
                    <Link to={getCollectionUrl(collection.uuid)} target="_blank">
                      {collection.name}
                    </Link>
                  ) : (
                    <strong>
                      {collection.name}
                    </strong>
                  )}
                </>
              )}
              {data?.source_uuid && (
                <>
                  {' '}{intl.formatMessage({ defaultMessage: 'From:' })}{' '}
                  {linksEnabled ? (
                    <Link to={getCollectionUrl(data.source_uuid)} target="_blank">
                      {data.source_name}
                    </Link>
                  ) : (
                    <strong>
                      {data.source_name}
                    </strong>
                  )}
                </>
              )}
              {data?.parent_uuid && (
                <>
                  {' '}{intl.formatMessage({ defaultMessage: 'To parent:' })}{' '}
                  {linksEnabled ? (
                    <Link to={getCollectionUrl(data.parent_uuid)} target="_blank">
                      {data.parent_uuid === '-1' ? intl.formatMessage({ defaultMessage: 'My collection' }) :
                        (data.parent_name ?? intl.formatMessage({ defaultMessage: 'All collections' }))}
                    </Link>
                  ) : (
                    <strong>
                      {data.parent_uuid === '-1' ? intl.formatMessage({ defaultMessage: 'My collection' }) :
                        (data.parent_name ?? intl.formatMessage({ defaultMessage: 'All collections' }))}
                    </strong>
                  )}
                </>
              )}
            </span>
          );
        }

        case 'delete': {
          return (
            <span>
              <i className="far fa-layer-group" />
              {intl.formatMessage({ defaultMessage: 'Deleted Collection' })}{' '}
              {(data.uuid || data.name) && (
                intl.formatMessage({ defaultMessage: '{name} (ID: {uuid})' }, { name: data.name, uuid: data.uuid })
              )}
            </span>
          );
        }

        default:
          return null;
      }
    }

    case 'collection_move': {
      return (
        <span>
          <i className="far fa-layer-group" />
          {intl.formatMessage({ defaultMessage: 'Moved files from Collection' })}
        </span>
      );
    }

    case 'collection_add_files':
    case 'collection_move_files':
    case 'collection_remove_files': {
      return (
        <span>
          <i className="far fa-layer-group" />
          {type === 'collection_add_files' && (
            intl.formatMessage({ defaultMessage: 'Added' })
          )}
          {type === 'collection_move_files' && (
            intl.formatMessage({ defaultMessage: 'Moved' })
          )}
          {type === 'collection_remove_files' && (
            intl.formatMessage({ defaultMessage: 'Removed' })
          )}
          {' '}
          {(linksEnabled && files) ? (
            <Link to={`/groups/${data.uuid}`} target="_blank">
              {intl.formatMessage({
                defaultMessage: '{files} {files, plural, one {file} other {files}}',
              }, { files })}
            </Link>
          ) : (
            <strong>
              {intl.formatMessage({
                defaultMessage: '{files} {files, plural, one {file} other {files}}',
              }, { files })}
            </strong>
          )}
          {' '}
          {type === 'collection_add_files' && (
            intl.formatMessage({ defaultMessage: 'to' })
          )}
          {(type === 'collection_move_files' || type === 'collection_remove_files') && (
            intl.formatMessage({ defaultMessage: 'from' })
          )}
          {' '}
          {intl.formatMessage({ defaultMessage: 'Collection' })}{' '}
          {data.collection && (
            <>
              {linksEnabled ? (
                <Link to={`/collections/${data.collection.uuid}`} target="_blank">
                  {data.collection.name}
                </Link>
              ) : (
                <strong>
                  {data.collection.name}
                </strong>
              )}
            </>
          )}
          {(type === 'collection_move_files' && data.target_collection) && (
            <>
              {' '}
              {intl.formatMessage({ defaultMessage: 'to Collection' })}{' '}
              {linksEnabled ? (
                <Link to={`/collections/${data.target_collection.uuid}`} target="_blank">
                  {data.target_collection.name}
                </Link>
              ) : (
                <strong>
                  {data.target_collection.name}
                </strong>
              )}
            </>
          )}
        </span>
      );
    }

    default:
      return (
        <span>
          {type}{' '}
          {action}
        </span>
      );
  }
};

ActionsItemText.propTypes = {
  action: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
};

export default ActionsItemText;
